import { CommandBlock } from "@/components/CommandBlock";
import { SetupStep } from "@/components/SetupStep";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";

function roleBindingCommand(member: string) {
  return [
    "gcloud projects add-iam-policy-binding <project-id> \\",
    `  --member serviceAccount:${member} \\`,
    "  --role roles/container.viewer",
  ].join("\n");
}

function clientEmailOf(serviceAccountKey: string): string | null {
  try {
    const email = (JSON.parse(serviceAccountKey) as { client_email?: unknown })
      .client_email;

    return typeof email === "string" && email.trim() ? email.trim() : null;
  } catch {
    return null;
  }
}

export function GkeAccessSteps({
  credentialType,
  serviceAccountKey,
  keptClientEmail,
}: {
  credentialType: string;
  serviceAccountKey: string;
  keptClientEmail: string | null;
}) {
  const { copiedField, copyToClipboard } = useCopyToClipboard();

  if (
    credentialType !== "GoogleIdentity" &&
    credentialType !== "GoogleServiceAccount"
  ) {
    return null;
  }

  const ownIdentity = credentialType === "GoogleIdentity";
  const member = ownIdentity
    ? "<client-email>"
    : (clientEmailOf(serviceAccountKey) ?? keptClientEmail ?? "<client-email>");

  return (
    <ol className="mt-2">
      {ownIdentity && (
        <SetupStep
          label="1"
          state="current"
          title="Give each gateway or connector a service account"
        >
          <p className="mt-1 text-sm text-foreground/70">
            Attach a service account where each gateway or connector runs. Use
            the VM's service account on Compute Engine, with the{" "}
            <code className="text-foreground">cloud-platform</code> and{" "}
            <code className="text-foreground">userinfo.email</code> access
            scopes, or Workload Identity Federation on GKE. The service account
            doesn't need any roles of its own.
          </p>
        </SetupStep>
      )}
      <SetupStep
        label={ownIdentity ? "2" : "1"}
        state="current"
        title="Give the service account access to the cluster"
        last
      >
        <p className="mt-1 mb-2 text-sm text-foreground/70">
          Grant it a role on the cluster's project. Replace{" "}
          <code className="text-foreground">{"<project-id>"}</code> with the
          project's ID
          {ownIdentity && (
            <>
              , and <code className="text-foreground">{"<client-email>"}</code>{" "}
              with the service account's email
            </>
          )}
          .
        </p>
        <CommandBlock
          command={roleBindingCommand(member)}
          field="gkeRoleBinding"
          copiedField={copiedField}
          onCopy={copyToClipboard}
        />
      </SetupStep>
    </ol>
  );
}
